import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/utils/axios";
import { formatDateTime } from "@/utils/formatDateTime";
import cls from "./UserPostsList.module.scss";

const LIMIT = 10;

const UserPostsList = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    totalPages: 1,
    hasNextPage: false,
  });

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(`/v1/admin/members/single/${userId}/posts`, {
          params: { page, limit: LIMIT },
        });

        if (!mounted) return;

        setUser(res.data?.user || null);
        setPosts(res.data?.posts || []);
        setMeta({
          total: res.data?.total || 0,
          totalPages: res.data?.totalPages || 1,
          hasNextPage: !!res.data?.hasNextPage,
        });
      } catch (err) {
        if (!mounted) return;
        setError(err?.response?.data?.message || "Failed to load posts.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (userId) run();

    return () => {
      mounted = false;
    };
  }, [userId, page]);

  const renderDateTime = (value) => {
    const formatted = formatDateTime(value);
    if (!formatted) return "—";
    return `${formatted.day}${formatted.suffix} ${formatted.month} ${formatted.year}, ${formatted.time}`;
  };

  const getTagClass = (group, value) => {
    const normalized = String(value || "").toLowerCase();
    return `${cls.tag} ${cls[`${group}-${normalized}`] || cls[`${group}-default`]}`;
  };

  const getDisplayStatus = (post) => {
    const isScheduled = post.publishMode === "scheduled";
    const isProcessed =
      String(post.scheduleStatus || "").toLowerCase() === "processed";

    if (isScheduled && !isProcessed) return "queued";
    if (isScheduled && isProcessed) return "published";
    return post.status || "unknown";
  };

  const getDateRows = (post) => {
    const isScheduled = post.publishMode === "scheduled";
    const isProcessed =
      String(post.scheduleStatus || "").toLowerCase() === "processed";

    if (!isScheduled) {
      return [{ label: "Created", value: renderDateTime(post.createdAt) }];
    }

    if (!isProcessed) {
      return [
        { label: "Created", value: renderDateTime(post.createdAt) },
        { label: "Scheduled", value: renderDateTime(post.publishAt) },
      ];
    }

    return [{ label: "Published", value: renderDateTime(post.publishedAt) }];
  };

  const stats = useMemo(() => {
    const result = {
      total: posts.length,
      publicCount: 0,
      clubCount: 0,
      deletedCount: 0,
    };

    posts.forEach((post) => {
      if (post.visibility === "public") result.publicCount += 1;
      if (post.visibility === "club") result.clubCount += 1;
      if (post.deletedAt) result.deletedCount += 1;
    });

    return result;
  }, [posts]);

  return (
    <div className={cls.page}>
      <div className={cls.bgGlowOne} />
      <div className={cls.bgGlowTwo} />

      <header className={cls.topbar}>
        <div className={cls.topbarLeft}>
          <button
            className={cls.backBtn}
            type="button"
            onClick={() => navigate(`/admin/members/single/${userId}`)}
          >
            <span className={cls.btnIcon}>←</span>
            <span>Back to Member</span>
          </button>

          <div className={cls.titleWrap}>
            <p className={cls.eyebrow}>Admin Post Manager</p>
            <h1 className={cls.title}>
              Member Posts {user?.username ? `(@${user.username})` : ""}
            </h1>
          </div>
        </div>

        <button
          type="button"
          className={cls.createBtn}
          onClick={() =>
            navigate(`/admin/members/single/posts/${userId}/create`)
          }
        >
          Create New Post
        </button>
      </header>

      {!loading && !error && (
        <section className={`${cls.card} ${cls.heroCard}`}>
          <div className={cls.heroTop}>
            <div>
              <p className={cls.sectionEyebrow}>Overview</p>
              <h2 className={cls.sectionTitle}>Post Summary</h2>
            </div>

            <div className={cls.memberPill}>
              <span className={cls.memberDot} />
              <span>
                {user?.username ? `@${user.username}` : "Unknown member"}
              </span>
            </div>
          </div>

          <div className={cls.statsRow}>
            <div className={cls.statCard}>
              <span className={cls.statLabel}>This Page</span>
              <strong className={cls.statValue}>{stats.total}</strong>
            </div>

            <div className={cls.statCard}>
              <span className={cls.statLabel}>Public</span>
              <strong className={cls.statValue}>{stats.publicCount}</strong>
            </div>

            <div className={cls.statCard}>
              <span className={cls.statLabel}>Club</span>
              <strong className={cls.statValue}>{stats.clubCount}</strong>
            </div>

            <div className={cls.statCard}>
              <span className={cls.statLabel}>Deleted</span>
              <strong className={cls.statValue}>{stats.deletedCount}</strong>
            </div>
          </div>
        </section>
      )}

      {loading && (
        <div className={`${cls.stateBox} ${cls.loadingBox}`}>
          <span className={cls.loader} />
          <span>Loading posts...</span>
        </div>
      )}

      {!loading && error && (
        <div className={`${cls.stateBox} ${cls.errorBox}`}>{error}</div>
      )}

      {!loading && !error && (
        <>
          <section className={cls.card}>
            <div className={cls.cardHead}>
              <div>
                <p className={cls.sectionEyebrow}>All Records</p>
                <h2 className={cls.sectionTitle}>Posts List</h2>
              </div>

              <div className={cls.tableMeta}>
                <span>Total: {meta.total}</span>
                <span>
                  Page {page} / {meta.totalPages}
                </span>
              </div>
            </div>

            <div className={cls.mobileList}>
              {posts.map((post) => {
                const displayStatus = getDisplayStatus(post);
                const dateRows = getDateRows(post);

                return (
                  <article key={post.id} className={cls.mobileCard}>
                    <div className={cls.mobileRow}>
                      <span className={cls.fieldLabel}>Type</span>
                      <span className={getTagClass("type", post.type)}>
                        {post.type || "unknown"}
                      </span>
                    </div>

                    <div className={cls.mobileRow}>
                      <span className={cls.fieldLabel}>Visibility</span>
                      <span
                        className={getTagClass("visibility", post.visibility)}
                      >
                        {post.visibility || "unknown"}
                      </span>
                    </div>

                    {post.visibility === "club" && (
                      <div className={cls.mobileInfoLine}>
                        <strong>Club:</strong> {post.clubName || "Unknown club"}
                      </div>
                    )}

                    <div className={cls.mobileRow}>
                      <span className={cls.fieldLabel}>Status</span>
                      <div className={cls.statusWrap}>
                        <span className={getTagClass("status", displayStatus)}>
                          {displayStatus}
                        </span>
                        {post.deletedAt && (
                          <span className={getTagClass("status", "deleted")}>
                            deleted
                          </span>
                        )}
                      </div>
                    </div>

                    {dateRows.map((row) => (
                      <div
                        key={`${post.id}-${row.label}`}
                        className={cls.mobileInfoLine}
                      >
                        <strong>{row.label}:</strong> {row.value}
                      </div>
                    ))}

                    <button
                      type="button"
                      className={cls.viewBtn}
                      onClick={() =>
                        navigate(
                          `/admin/members/single/posts/${userId}/${post.id}`,
                        )
                      }
                    >
                      View Post
                    </button>
                  </article>
                );
              })}

              {posts.length === 0 && (
                <div className={cls.emptyBox}>
                  No posts found for this member.
                </div>
              )}
            </div>

            <div className={cls.tableWrap}>
              <table className={cls.table}>
                <thead>
                  <tr>
                    <th>Post Type / Visibility</th>
                    <th>Status / Date & Time</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {posts.map((post) => {
                    const displayStatus = getDisplayStatus(post);
                    const dateRows = getDateRows(post);

                    return (
                      <tr key={post.id}>
                        <td>
                          <div className={cls.stackLine}>
                            <span className={getTagClass("type", post.type)}>
                              {post.type || "unknown"}
                            </span>
                            <strong>{post.visibility || "unknown"}</strong>
                          </div>

                          {post.visibility === "club" && (
                            <div className={cls.infoLine}>
                              <strong>Club:</strong>{" "}
                              {post.clubName || "Unknown club"}
                            </div>
                          )}
                          {post.deletedAt && (
                            <span className={getTagClass("status", "deleted")}>
                              deleted
                            </span>
                          )}
                        </td>

                        <td>
                          <div className={cls.stackLine}>
                            <span
                              className={getTagClass("status", displayStatus)}
                            >
                              {displayStatus}
                            </span>

                            {post.deletedAt && (
                              <span
                                className={getTagClass("status", "deleted")}
                              >
                                deleted
                              </span>
                            )}
                          </div>

                          {dateRows.map((row) => (
                            <div
                              key={`${post.id}-${row.label}`}
                              className={cls.infoLine}
                            >
                              <strong>{row.label}:</strong> {row.value}
                            </div>
                          ))}
                        </td>

                        <td>
                          <button
                            type="button"
                            className={cls.viewBtn}
                            onClick={() =>
                              navigate(
                                `/admin/members/single/posts/${userId}/${post.id}`,
                              )
                            }
                          >
                            View Post
                          </button>
                        </td>
                      </tr>
                    );
                  })}

                  {posts.length === 0 && (
                    <tr>
                      <td colSpan={3} className={cls.empty}>
                        No posts found for this member.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className={cls.pagination}>
            <button
              type="button"
              className={cls.pageBtn}
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>

            <span className={cls.pageInfo}>
              Page {page} of {meta.totalPages} · {meta.total} total
            </span>

            <button
              type="button"
              className={cls.pageBtn}
              disabled={!meta.hasNextPage}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default UserPostsList;
